import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SchoolInfoForm } from "@/components/auth/SchoolInfoForm";
import { PersonalInfoForm } from "@/components/auth/PersonalInfoForm";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/context/auth-context";
import { Info, UserRound, School, Briefcase } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { createLogger } from "@/lib/utils/logger";
import { PositionSelect } from "@/components/auth/PositionSelect";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { IncompleteField } from "@/lib/auth/utils/profile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const logger = createLogger('auth', 'ProfileCompletionPopup');

interface ProfileCompletionPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incompleteFields: IncompleteField[];
  onComplete?: () => void;
}

const fieldIcons = {
  personal: UserRound,
  school: School,
  position: Briefcase
};

const fieldLabels: Record<IncompleteField, string> = {
  personal: "Personal Information",
  school: "School Information",
  position: "Position Information"
};

export function ProfileCompletionPopup({ 
  open, 
  onOpenChange, 
  incompleteFields = [],
  onComplete 
}: ProfileCompletionPopupProps) {
  const { user, profile, updateAuthState } = useAuth();
  const [activeTab, setActiveTab] = useState<IncompleteField>(() => {
    return incompleteFields && incompleteFields.length > 0 ? incompleteFields[0] : 'personal';
  });
  const [firstName, setFirstName] = useState(profile?.first_name || "");
  const [lastName, setLastName] = useState(profile?.last_name || "");
  const [schoolDistrict, setSchoolDistrict] = useState(profile?.school_district || "");
  const [customSchoolDistrict, setCustomSchoolDistrict] = useState("");
  const [school, setSchool] = useState(profile?.school || "");
  const [customSchool, setCustomSchool] = useState("");
  const [position, setPosition] = useState(profile?.position || "");
  const [customPosition, setCustomPosition] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  // Check if the user authenticated with OAuth and pre-fill name if available
  useEffect(() => {
    if (user) {
      const checkOAuthStatus = async () => {
        const { data, error } = await supabase.auth.getUser();
        if (!error && data?.user) {
          const identities = data.user.identities || [];
          const usedOAuth = identities.some(identity => 
            identity.provider !== 'email'
          );
          setIsOAuthUser(usedOAuth);
          
          if (usedOAuth) {
            const metadata = data.user.user_metadata;
            if (metadata) {
              if (metadata.full_name) {
                const nameParts = metadata.full_name.split(' ');
                if (nameParts.length > 0) {
                  setFirstName(metadata.name || nameParts[0] || firstName);
                  setLastName(nameParts.length > 1 ? nameParts.slice(1).join(' ') : lastName);
                }
              } else {
                setFirstName(metadata.name || metadata.given_name || firstName);
                setLastName(metadata.family_name || lastName);
              }
            }
          }
        }
      };
      
      checkOAuthStatus();
    }
  }, [user, firstName, lastName]);

  // Update activeTab when incompleteFields changes
  useEffect(() => {
    if (incompleteFields && incompleteFields.length > 0 && !incompleteFields.includes(activeTab)) {
      setActiveTab(incompleteFields[0]);
    }
  }, [incompleteFields, activeTab]);

  // Handle submission of the profile form
  const handleSubmit = async () => {
    if (!user) return;
    
    try {
      setIsSubmitting(true);
      
      // Prepare update data based on incomplete fields
      const updateData: Record<string, any> = {
        updated_at: new Date().toISOString()
      };

      if (incompleteFields.includes('personal')) {
        updateData.first_name = firstName;
        updateData.last_name = lastName;
      }

      if (incompleteFields.includes('school')) {
        updateData.school_district = schoolDistrict === "Other" ? customSchoolDistrict : schoolDistrict;
        updateData.school = school === "Other" ? customSchool : school;
      }

      if (incompleteFields.includes('position')) {
      let finalPosition = position === "Other" ? customPosition : position;
      if (finalPosition.includes('|')) {
        finalPosition = finalPosition.split('|')[0];
      }
        updateData.position = finalPosition;
      }
      
      logger.info(`Updating profile with values: ${JSON.stringify(updateData)}`);
      
      // Update profile in Supabase
      const { data, error } = await supabase
        .from("profiles")
        .update(updateData)
        .eq("id", user.id)
        .select();
      
      if (error) {
        throw error;
      }
      
      logger.info(`Profile update successful: ${JSON.stringify(data)}`);
      
      // Refresh auth state to get updated profile
      await updateAuthState(user, false);
      
      toast({
        title: "Profile Updated",
        description: "Your profile information has been successfully updated.",
      });
      
      // Close dialog
      onOpenChange(false);
      
      // Call onComplete callback if provided
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "An error occurred updating your profile";
      logger.error("Profile update error:", message);
      
      toast({
        variant: "destructive",
        title: "Profile Update Failed",
        description: message,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no user or no incomplete fields, don't show anything
  if (!user || !incompleteFields || incompleteFields.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
            {activeTab && React.createElement(fieldIcons[activeTab], { className: "h-5 w-5 text-[#004161]" })}
            <span className="text-[#004161] font-serif">Complete Your Profile</span>
            </DialogTitle>
          <DialogDescription className="font-sans text-[#004161]/70">
            Please complete the following information to get the most out of the CEU Navigator.
            </DialogDescription>
          </DialogHeader>
          
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as IncompleteField)} className="mt-4">
          <TabsList className="w-full">
            {incompleteFields.map((field) => (
              <TabsTrigger 
                key={field} 
                value={field}
                className="flex items-center gap-2 data-[state=active]:text-[#004161] data-[state=active]:font-serif"
              >
                {React.createElement(fieldIcons[field], { className: "h-4 w-4" })}
                {fieldLabels[field]}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="personal" className="space-y-4 mt-4">
                <PersonalInfoForm
                  firstName={firstName}
                  lastName={lastName}
              email=""
                  setFirstName={setFirstName}
                  setLastName={setLastName}
                  setEmail={() => {}}
              disabled={isSubmitting}
              hideEmailPassword
            />
          </TabsContent>

          <TabsContent value="school" className="space-y-4 mt-4">
              <SchoolInfoForm
                schoolDistrict={schoolDistrict}
                school={school}
                customSchoolDistrict={customSchoolDistrict}
                customSchool={customSchool}
                setSchoolDistrict={setSchoolDistrict}
                setSchool={setSchool}
                setCustomSchoolDistrict={setCustomSchoolDistrict}
                setCustomSchool={setCustomSchool}
                disabled={isSubmitting}
              />
          </TabsContent>
              
          <TabsContent value="position" className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label htmlFor="position" className="text-[#004161] font-serif">Position</Label>
                <PositionSelect
                  position={position}
                  onChange={setPosition}
                  customPosition={customPosition}
                  onCustomPositionChange={setCustomPosition}
                  disabled={isSubmitting}
                />
              </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-[#004161]/10 hover:border-[#004161]/20 text-[#004161] hover:text-[#004161]/80 font-serif"
          >
            Complete Later
                </Button>
          <Button 
            onClick={handleSubmit}
            className="bg-[#7497ab] hover:bg-[#7497ab]/90 text-white transition-all duration-300 font-serif"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
